import { useState, useCallback } from 'react'
import Cropper from 'react-easy-crop'
import type { Area, Point } from 'react-easy-crop'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { Loader2, ZoomIn, RotateCw } from 'lucide-react'
import { getCroppedImg, blobToFile } from '@/utils/cropImage'

interface ImageCropDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  imageSrc: string
  onCropComplete: (croppedFile: File) => void
  aspectRatio?: number
  outputSize?: number
  cropShape?: 'round' | 'rect'
  title?: string
  description?: string
}

export function ImageCropDialog({
  open,
  onOpenChange,
  imageSrc,
  onCropComplete,
  aspectRatio = 1, // Quadrado por padrão
  outputSize = 256,
  cropShape = 'round',
  title = 'Ajustar Foto de Perfil',
  description = 'Arraste para posicionar e use o zoom para enquadrar sua foto.'
}: ImageCropDialogProps) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  const onCropChange = useCallback((location: Point) => {
    setCrop(location)
  }, [])

  const onZoomChange = useCallback((newZoom: number) => {
    setZoom(newZoom)
  }, [])

  const onRotationChange = useCallback((newRotation: number) => {
    setRotation(newRotation)
  }, [])

  const onCropAreaComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return

    setIsProcessing(true)
    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation, outputSize)
      const croppedFile = blobToFile(croppedBlob, `avatar_${Date.now()}.jpg`)
      onCropComplete(croppedFile)
      onOpenChange(false)
    } catch (error) {
      console.error('Erro ao recortar imagem:', error)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleCancel = () => {
    // Resetar estados
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setRotation(0)
    onOpenChange(false)
  }

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[500px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-lg md:text-xl">{title}</DialogTitle>
          <DialogDescription className="text-sm">
            {description}
          </DialogDescription>
        </DialogHeader>

        {/* Área de Crop */}
        <div className="relative w-full h-64 md:h-80 bg-muted rounded-lg overflow-hidden">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspectRatio}
            cropShape={cropShape}
            showGrid={false}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onRotationChange={onRotationChange}
            onCropComplete={onCropAreaComplete}
            classes={{
              containerClassName: 'rounded-lg',
              cropAreaClassName: 'border-2 border-primary'
            }}
          />
        </div>

        {/* Controles */}
        <div className="space-y-4 py-2">
          {/* Zoom */}
          <div className="flex items-center gap-3">
            <ZoomIn className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-sm text-muted-foreground w-12">Zoom</span>
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.1}
              onValueChange={(values) => setZoom(values[0])}
              className="flex-1"
              disabled={isProcessing}
            />
            <span className="text-sm text-muted-foreground w-10 text-right">
              {Math.round(zoom * 100)}%
            </span>
          </div>

          {/* Rotação */}
          <div className="flex items-center gap-3">
            <RotateCw className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-sm text-muted-foreground w-12">Girar</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRotate}
              disabled={isProcessing}
              className="px-3"
            >
              +90°
            </Button>
            <span className="text-sm text-muted-foreground">
              {rotation}°
            </span>
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isProcessing}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isProcessing || !croppedAreaPixels}
            className="w-full sm:w-auto"
          >
            {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
